import {
  CreateUserType, UserAdditionalSettings,
  LtiUsersExistType,
  RegenerateSkipLoginCodeData,
  UserType,
  UserWithProgressType,
} from "@/types/AuthTypes";
import { ResponseType } from "@/types/CommonTypes";
import { GroupType } from "@/types/GroupTypes";
import { PermissionType } from "@/types/PermissionTypes";
import axiosInstance from "./axios";
import axios from "axios";

export const usersApi = {
  getUsers: async (
    page?: number,
    limit?: number,
    searchTerm?: string,
    activeFilter?: number | null,
    ltiFilter?: number | null,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal
  ): Promise<ResponseType<UserType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (activeFilter !== null && activeFilter !== undefined) {
        queryParams.append("active", activeFilter.toString());
      }
      if (ltiFilter !== null && ltiFilter !== undefined) {
        queryParams.append("include_lti", ltiFilter.toString());
      }
      if (sortBy) {
        // Map frontend keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          // No field mappings needed - frontend keys match backend field names
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }
      const response = await axiosInstance.get(
        `/users?${queryParams.toString()}`,
        { signal }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },
  getById: async (userId: number): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /** Start impersonating a user. Returns an impersonation JWT + minimal target info. */
  impersonate: async (
    userId: number,
  ): Promise<
    ResponseType<{
      token: string;
      user: {
        id: number;
        first_name: string | null;
        sur_name: string | null;
        username: string | null;
        role: { label: string; value: string } | null;
      };
    }>
  > => {
    try {
      const response = await axiosInstance.post(`/users/${userId}/impersonate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  isUsernameAvailable: async (
    username: string
  ): Promise<ResponseType<boolean>> => {
    try {
      const response = await axiosInstance.get(
        `/users/username/is-available?username=${username}`
      );
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: true,
        };
      } else {
        return {
          success: false,
          message: response.data.message,
          data: false,
        };
      }
    } catch (error) {
      throw error;
    }
  },
  createUser: async (
    user: UserType | CreateUserType
  ): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.post("/auth/create", user);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  update: async (
    userId: number,
    data: UserType | CreateUserType
  ): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.put(`/users/${userId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /**
   * The call settings and landing page in additional_settings. Merged on the server, which
   * refuses the skip-login code and consent by name — a form never sends what it does not own.
   */
  patchSettings: async (
    userId: number,
    patch: Record<string, unknown>,
  ): Promise<ResponseType<{ additional_settings: UserAdditionalSettings; warnings: string[] }>> => {
    const response = await axiosInstance.patch(`/users/${userId}/settings`, patch);
    return response.data;
  },
  updateActive: async (
    userId: number,
    active: boolean
  ): Promise<ResponseType<UserType>> => {
    try {
      const response = await axiosInstance.patch(`/users/${userId}/active`, { active });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  regenerateSkipLoginCode: async (
    userId: number
  ): Promise<ResponseType<RegenerateSkipLoginCodeData>> => {
    try {
      const response = await axiosInstance.post(
        `/users/${userId}/skip-login-code/regenerate`,
        {}
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updatePassword: async (
    userId: number,
    password: string
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.put("/auth/update-user-password", {
        userId: userId,
        newPassword: password,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getAssignableUsers: async (
    clientId: number
  ): Promise<ResponseType<UserType[]>> => {
    try {
      const response = await axiosInstance.get(
        `/users/assignable?client_id=${clientId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getUserGroups: async (
    userId: number,
    page?: number,
    limit?: number
  ): Promise<ResponseType<GroupType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page.toString());
      if (limit) queryParams.append("limit", limit.toString());

      const response = await axiosInstance.get(
        `/users/${userId}/groups?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getUserPermissionOverrides: async (
    userId: number
  ): Promise<ResponseType<PermissionType[]>> => {
    try {
      const response = await axiosInstance.get(`/users/${userId}/permissions`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updateUserPermissionOverrides: async (
    userId: number,
    payload: { grants: number[]; denies: number[] } | number[]
  ): Promise<ResponseType<{ permission_ids?: number[]; grant_ids?: number[]; deny_ids?: number[] }>> => {
    try {
      const body = Array.isArray(payload)
        ? { permissions: payload }
        : { grants: payload.grants, denies: payload.denies };
      const response = await axiosInstance.put(`/users/${userId}/permissions`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getUsersProgress: async (
    page?: number,
    limit?: number,
    searchTerm?: string,
    activeFilter?: number | null,
    ltiFilter?: number | null,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal
  ): Promise<ResponseType<UserWithProgressType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (activeFilter !== null && activeFilter !== undefined) {
        queryParams.append("active", activeFilter.toString());
      }
      if (ltiFilter !== null && ltiFilter !== undefined) {
        queryParams.append("include_lti", ltiFilter.toString());
      }
      if (sortBy) {
        // Map frontend keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          // No field mappings needed - frontend keys match backend field names
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }
      const response = await axiosInstance.get(
        `/users/progress?${queryParams.toString()}`,
        { signal }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },
  getUsersByClientId: async (
    clientId: number,
    page?: number,
    limit?: number,
    searchTerm?: string,
    activeFilter?: number | null,
    ltiFilter?: number | null,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal
  ): Promise<ResponseType<UserWithProgressType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (activeFilter !== null && activeFilter !== undefined) {
        queryParams.append("active", activeFilter.toString());
      }
      if (ltiFilter !== null && ltiFilter !== undefined) {
        queryParams.append("include_lti", ltiFilter.toString());
      }
      if (sortBy) {
        // Map frontend keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          // No field mappings needed - frontend keys match backend field names
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }
      const response = await axiosInstance.get(
        `/users/${clientId}/client?${queryParams.toString()}`,
        { signal }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },
  checkLtiUsersExist: async (): Promise<ResponseType<LtiUsersExistType>> => {
    try {
      const response = await axiosInstance.get("/users/lti-users-exist");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getLanguages: async (userId: number): Promise<ResponseType<{ allowed_languages: string[] }>> => {
    try {
      const response = await axiosInstance.get(`/users/${userId}/languages`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setLanguages: async (
    userId: number,
    languageCodes: string[]
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post(`/users/${userId}/languages`, {
        languages: languageCodes,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  checkLanguagePermission: async (
    userId: number,
    languageCode: string
  ): Promise<ResponseType<boolean>> => {
    try {
      const response = await axiosInstance.get(
        `/users/${userId}/languages/check`,
        {
          params: { language_code: languageCode },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  clearLanguages: async (userId: number): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(`/users/${userId}/languages`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteUser: async (userId: number): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  sendResetPasswordEmail: async (
    userId: number
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post(
        `/users/${userId}/send-reset-password-email`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
